import { useWindowDimensions } from 'react-native'
import Animated, { useAnimatedStyle } from 'react-native-reanimated'
import useAnimatedToggle from '@ui/hooks/useAnimatedToggle'
import Gradient from '@ui/effects/Gradient'

import type { MeasurementOvalNormalizedFrame } from '../Oval/types'
import { doesOvalOverlapTopBand, resolveOvalLayout } from '../Oval/utils'
import MessageItem from './components/MessageItem'
import MeasurementProgress from './components/MeasurementProgress'
import {
  MESSAGES_GRADIENT_DEFAULT,
  MESSAGES_GRADIENT_STRONG,
  MESSAGES_GRADIENT_STRONG_LOCATIONS,
  MESSAGES_HEIGHT,
} from './styles'
import { Measurement, MeasurementMessage } from './types'

const messages = Object.values(Measurement.Messages)

type Props = {
  isOpen: boolean
  message: MeasurementMessage | null
  progress: number
  /** Camera-viewport oval; when its scale would cover this strip, the fade goes full-strength. */
  ovalFrame?: MeasurementOvalNormalizedFrame
  /** Same multiplier Oval uses, so the fade tracks debug / camera scale. */
  ovalScale?: number
}

export default function Messages({
  isOpen,
  message,
  progress,
  ovalFrame,
  ovalScale = 1,
}: Props) {
  const animation = useAnimatedToggle(isOpen)
  const { width, height } = useWindowDimensions()
  const ovalLayout = resolveOvalLayout(width, height, ovalFrame, ovalScale)
  const isStrongGradient = ovalLayout
    ? doesOvalOverlapTopBand(ovalLayout, MESSAGES_HEIGHT)
    : false

  const styleInteraction = useAnimatedStyle(() => ({
    pointerEvents: animation.value === 0 ? 'none' : 'auto',
  }))

  const styleContent = useAnimatedStyle(() => ({
    opacity: animation.value,
  }))

  return (
    <Animated.View
      className="relative self-center overflow-hidden"
      style={[styleInteraction, { width: '100%', height: MESSAGES_HEIGHT }]}
    >
      <Animated.View className="absolute inset-0" style={[styleContent]}>
        <Gradient
          colors={
            isStrongGradient
              ? MESSAGES_GRADIENT_STRONG
              : MESSAGES_GRADIENT_DEFAULT
          }
          locations={
            isStrongGradient ? MESSAGES_GRADIENT_STRONG_LOCATIONS : undefined
          }
          start={[0.5, 0]}
          end={[0.5, 1]}
        />
        {messages.map((item) => (
          <MessageItem key={item} message={item} active={message} />
        ))}
        <MeasurementProgress progress={progress} />
      </Animated.View>
    </Animated.View>
  )
}
